import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { UNAUTHORIZED_EVENT } from '../api/client';
import { LoginScreen } from './LoginScreen';
import { useAuth } from '../state/useAuth';

/**
 * Blocks the interactive app (StartView/ProposalView) behind the existing
 * sign-in dialog once the server started requiring a session for everything
 * under /api — see docs/auth-org-cobrand.md "sessions and ownership". NOT
 * used on /share/:token or /print/:id, which stay reachable with no session
 * by design (App.tsx renders those before this gate).
 *
 * Two ways in: the normal `/api/auth/status` probe on mount, and an
 * immediate switch on ANY 401 from anywhere in the app (a session that
 * expired or was signed out in another tab) — see UNAUTHORIZED_EVENT.
 */
export function AuthGate({ children }: { children: ReactNode }): ReactElement | null {
  const auth = useAuth();
  const [forcedSignedOut, setForcedSignedOut] = useState(false);

  useEffect(() => {
    const onUnauthorized = () => setForcedSignedOut(true);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  // A fresh sign-in (this gate's own modal, or the sidebar's) clears the
  // forced flag the moment the status store reports signed in again.
  useEffect(() => {
    if (auth.status?.signedIn) setForcedSignedOut(false);
  }, [auth.status?.signedIn]);

  // Status hasn't resolved yet — render nothing rather than flashing the
  // sign-in dialog for a signed-in rep on every load.
  if (auth.status === null) return null;

  const signedOut = forcedSignedOut || !auth.status.signedIn;
  if (signedOut) {
    return <LoginScreen auth={auth} />;
  }

  return <>{children}</>;
}
